import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getFirestoreDb } from '@/lib/firebase';
import { useOrg } from '@/contexts/OrgContext';
import { useToast } from '@/contexts/ToastContext';
import { toastError } from '@/lib/toastErrors';
import { useUnsavedWarning } from '@/hooks/useUnsavedWarning';
import { useSettingsAccess } from '@/components/settings/useSettingsAccess';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { SettingsSection } from '@/components/settings/SettingsSection';

const MAX_LOGO_BYTES = 400 * 1024;
const ACCEPT = 'image/png,image/jpeg,image/webp';

export function BrandingSection() {
  const { t } = useTranslation();
  const toast = useToast();
  const { org, brandingDisplayName, brandingLogoUrl } = useOrg();
  const { orgId, online, canEdit } = useSettingsAccess();
  const [displayName, setDisplayName] = useState('');
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoDirty, setLogoDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setDisplayName(brandingDisplayName ?? '');
    if (!logoDirty) setLogoPreview(brandingLogoUrl);
    setDirty(false);
  }, [brandingDisplayName, brandingLogoUrl, logoDirty]);

  useUnsavedWarning(dirty || logoDirty);

  if (!orgId || !canEdit) return null;

  async function onFileChange(file: File | undefined) {
    if (!file) return;
    if (file.size > MAX_LOGO_BYTES) {
      toast.error(t('settings.logoTooLarge'), 'branding');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setLogoPreview(reader.result as string);
      setLogoDirty(true);
    };
    reader.readAsDataURL(file);
  }

  async function save() {
    if (!orgId || !online) return;
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        displayName: displayName.trim() || null,
        updatedAt: serverTimestamp()
      };
      if (logoDirty) payload.logoDataUrl = logoPreview ?? null;
      await setDoc(
        doc(getFirestoreDb(), 'organizations', orgId, 'settings', 'branding'),
        payload,
        { merge: true }
      );
      setLogoDirty(false);
      setDirty(false);
      toast.success(t('settings.brandingSaved'), 'branding');
    } catch (e) {
      toastError(toast, e, 'branding');
    } finally {
      setSaving(false);
    }
  }

  return (
    <SettingsSection id="settings-branding" title={t('settings.branding')}>
      <div>
        <Label htmlFor="brand-display-name">{t('settings.brandName')}</Label>
        <Input
          id="brand-display-name"
          className="mt-1 max-w-md"
          value={displayName}
          onChange={(e) => {
            setDisplayName(e.target.value);
            setDirty(true);
          }}
          placeholder={org?.name}
          disabled={!online}
        />
        <p className="mt-1 text-xs text-[var(--fv-text-muted)]">{t('settings.brandNameHint')}</p>
      </div>
      <div>
        <Label htmlFor="brand-logo">{t('settings.logo')}</Label>
        {logoPreview ? (
          <div className="mt-2 h-14 w-14 overflow-hidden rounded-[var(--fv-radius)] border border-[var(--fv-border)] bg-white">
            <img src={logoPreview} alt="" className="h-full w-full object-cover object-center" />
          </div>
        ) : null}
        <Input
          id="brand-logo"
          type="file"
          accept={ACCEPT}
          className="mt-2 max-w-md"
          disabled={!online}
          onChange={(e) => void onFileChange(e.target.files?.[0])}
        />
        <p className="mt-1 text-xs text-[var(--fv-text-muted)]">{t('settings.logoHint')}</p>
        {logoPreview ? (
          <Button type="button" variant="link" size="sm" className="mt-2" disabled={!online} onClick={() => { setLogoPreview(null); setLogoDirty(true); }}>
            {t('settings.logoRemove')}
          </Button>
        ) : null}
      </div>
      <Button type="button" disabled={saving || !online || (!dirty && !logoDirty)} onClick={() => void save()}>
        {t('common.save')}
      </Button>
    </SettingsSection>
  );
}
